import { Injectable, Signal, signal, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { MATOMO_CONTEXT_CHANGED_EVENT } from '../matomo/matomo-analytics.config';

/**
 * Parse a raw query param value into a host token suitable for Matomo.
 *
 * Behaviour:
 * - Accept any non-empty string (host may be an arbitrary identifier like
 *   `archicad_v1-2-3` or a third-party app name).
 * - Trim whitespace and normalize to lowercase.
 * - Return empty string for null/undefined/empty input.
 */
export function parseHostToken(raw: string | null | undefined): string {
  const v = raw?.trim();
  if (!v) return '';
  return v.toLowerCase();
}

export type EmbeddedSource = 'route' | 'query-param';

/** A simple string alias for Matomo/host values carried via query params or sessionStorage */

/**
 * Manages URL query parameter flags that affect application behavior
 * Example: ?embedded=1
 */
@Injectable({
  providedIn: 'root',
})
export class UrlFlagsService {
  // Use shared constant for the Matomo context changed event to avoid drift
  private readonly _embedded = signal<boolean>(false);
  private readonly _embeddedSource = signal<EmbeddedSource | null>(null);
  private readonly _customFlags = signal<Record<string, string>>({});
  private readonly _showCreateForm = signal<boolean>(false);
  private readonly _showLogout = signal<boolean>(false);
  private readonly _host = signal<string>('');

  /** Whether the app is running in embedded mode (e.g., iframe) */
  readonly embedded: Signal<boolean> = this._embedded.asReadonly();

  /** How embedded mode was activated: 'route' (path prefix), 'query-param' (?embedded=1), or null */
  readonly embeddedSource: Signal<EmbeddedSource | null> = this._embeddedSource.asReadonly();

  /** All custom flags as key-value pairs */
  readonly customFlags = this._customFlags.asReadonly();

  /** Whether the Create Task form dialog should open directly instead of notifying the host. */
  readonly showCreateForm: Signal<boolean> = this._showCreateForm.asReadonly();

  /** Whether the logout button is visible in embedded mode. */
  readonly showLogout: Signal<boolean> = this._showLogout.asReadonly();

  /**
   * Host flag used by Matomo integration, derived exclusively from `?host=`.
   * Never restored from storage — absent from the URL means an empty host context.
   */
  readonly host: Signal<string> = this._host.asReadonly();

  private readonly SESSION_KEY_CUSTOM_FLAGS = 'customFlags';
  private readonly RESERVED_FLAGS = new Set(['embedded', 'showCreateForm', 'showLogout', 'host']);

  private getCustomFlagsFromSearchParams(searchParams: URLSearchParams): Record<string, string> {
    const custom: Record<string, string> = {};

    searchParams.forEach((value, key) => {
      if (!this.RESERVED_FLAGS.has(key)) {
        custom[key] = value;
      }
    });

    return custom;
  }

  private initializeCustomFlags(searchParams: URLSearchParams): void {
    const currentFlags = this.getCustomFlagsFromSearchParams(searchParams);
    const stored = sessionStorage.getItem(this.SESSION_KEY_CUSTOM_FLAGS);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const storedFlags: Record<string, string> =
          parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};

        this.RESERVED_FLAGS.forEach(key => delete storedFlags[key]);
        this._customFlags.set(storedFlags);
      } catch {
        sessionStorage.removeItem(this.SESSION_KEY_CUSTOM_FLAGS);
      }

      return;
    }

    if (Object.keys(currentFlags).length > 0) {
      sessionStorage.setItem(this.SESSION_KEY_CUSTOM_FLAGS, JSON.stringify(currentFlags));
    }

    this._customFlags.set(currentFlags);
  }

  /**
   * Determines embedded mode purely from the current URL: `?embedded=1`, or a
   * `/embedded/` path segment (pathname or hash, for hash-based routing).
   */
  private computeEmbeddedFromLocation(searchParams: URLSearchParams): {
    embedded: boolean;
    source: EmbeddedSource | null;
  } {
    const isEmbeddedPath =
      (globalThis.location.pathname ?? '').includes('/embedded/') ||
      (globalThis.location.hash ?? '').includes('#/embedded/');

    if (isEmbeddedPath) {
      return { embedded: true, source: 'route' };
    }
    if (searchParams.get('embedded') === '1') {
      return { embedded: true, source: 'query-param' };
    }
    return { embedded: false, source: null };
  }

  /**
   * Fully recomputes `embedded` from the URL — never additive, so navigating away
   * from an embedded URL correctly clears the flag instead of leaving it "stuck".
   */
  private initializeEmbedded(searchParams: URLSearchParams): void {
    const { embedded, source } = this.computeEmbeddedFromLocation(searchParams);

    this._embedded.set(embedded);
    this._embeddedSource.set(source);
  }

  /**
   * Fully recomputes `host` from the URL — never additive, so navigating away from
   * a `?host=` URL correctly clears the flag instead of leaving it "stuck".
   */
  private initializeHost(searchParams: URLSearchParams): void {
    const hostToken = parseHostToken(searchParams.get('host'));
    const newHost = hostToken || '';

    this._host.set(newHost);
  }

  /**
   * Initialize flags from the current URL. Safe to call repeatedly — `embedded` and
   * `host` are always recomputed from the URL, never from storage, so stale context
   * cannot leak across navigations or across apps sharing the same origin.
   */
  initialize(): void {
    const searchParams = new URLSearchParams(globalThis.location.search);
    const previousEmbedded = this._embedded();
    const previousHost = this._host();
    this.initializeCustomFlags(searchParams);
    this.initializeEmbedded(searchParams);
    this.initializeHost(searchParams);
    if (previousEmbedded !== this._embedded() || previousHost !== this._host()) {
      this.notifyMatomoContextChanged();
    }
    const showCreateForm = searchParams.get('showCreateForm');
    this._showCreateForm.set(showCreateForm === 'true' || showCreateForm === '1');
    const showLogout = searchParams.get('showLogout');
    this._showLogout.set(showLogout === 'true' || showLogout === '1');
  }

  constructor() {
    try {
      // Listen to browser history navigation even when Angular Router is present.
      // This keeps URL-derived embedded/host state in sync on back/forward.
      globalThis.addEventListener?.('popstate', () => this.refreshFromCurrentUrl());

      const router = inject(Router, { optional: true });
      if (router) {
        router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
          this.refreshFromCurrentUrl();
        });
      }
    } catch {
      // ignore injection errors
    }
  }

  /**
   * Re-derives `embedded`/`host` from the current URL on every navigation, including
   * browser back/forward. Always fully recomputes both flags (see
   * `initializeEmbedded`/`initializeHost`) rather than only reacting to explicit
   * query params, so returning to a non-embedded URL reliably clears embedded mode.
   */
  private refreshFromCurrentUrl(): void {
    try {
      const searchParams = new URLSearchParams(globalThis.location.search);
      const previousEmbedded = this._embedded();
      const previousHost = this._host();
      this.initializeEmbedded(searchParams);
      this.initializeHost(searchParams);
      const contextChanged = previousEmbedded !== this._embedded() || previousHost !== this._host();
      if (contextChanged) {
        this.notifyMatomoContextChanged();
      }
      // Custom flags and other persisted flags are not overwritten on navigation to avoid surprising behavior
    } catch {
      // ignore
    }
  }

  /**
   * Programmatically set embedded mode in memory (not persisted to storage).
   * Useful for tests or callers that already know the source (e.g. a route path check).
   */
  setEmbedded(value: boolean, source: EmbeddedSource = 'route'): void {
    const previousValue = this._embedded();
    const previousSource = this._embeddedSource();

    this._embedded.set(value);
    this._embeddedSource.set(value ? source : null);

    if (previousValue !== this._embedded() || previousSource !== this._embeddedSource()) {
      this.notifyMatomoContextChanged();
    }
  }

  /**
   * Programmatically set the Matomo host flag in memory (not persisted to storage).
   */
  setHost(host: string | null): void {
    const prev = this._host();
    const hostToken = parseHostToken(host);

    this._host.set(hostToken || '');

    if (prev !== this._host()) {
      this.notifyMatomoContextChanged();
    }
  }

  private notifyMatomoContextChanged(): void {
    try {
      globalThis.dispatchEvent(new CustomEvent(MATOMO_CONTEXT_CHANGED_EVENT));
    } catch {
      // ignore
    }
  }

  /**
   * Get a custom flag value by key
   */
  getFlag(key: string): string | null {
    return this._customFlags()[key] ?? null;
  }

  /**
   * Returns embedded context from either the current URL or an encoded `redirectTo` target.
   * This is used in guards where the current page may be `/terms` but the next destination
   * (stored in `redirectTo`) is an embedded project route.
   */
  embeddedFromCurrentOrRedirectTo(): boolean {
    if (this._embedded()) {
      return true;
    }

    const searchParams = new URLSearchParams(globalThis.location.search);
    const redirectTo = searchParams.get('redirectTo');

    return this.isEmbeddedRedirectTarget(redirectTo);
  }

  /**
   * Check if a boolean flag is enabled (value is '1' or 'true')
   */
  isFlagEnabled(key: string): boolean {
    const value = this.getFlag(key);
    return value === '1' || value === 'true';
  }

  private isEmbeddedRedirectTarget(rawRedirectTo: string | null): boolean {
    if (!rawRedirectTo) {
      return false;
    }

    const candidates = [rawRedirectTo];

    try {
      const decoded = decodeURIComponent(rawRedirectTo);
      if (decoded !== rawRedirectTo) {
        candidates.push(decoded);
      }
    } catch {
      // keep original value only
    }

    return candidates.some(candidate => this.isEmbeddedUrl(candidate));
  }

  private isEmbeddedUrl(urlLike: string): boolean {
    if (urlLike.includes('/embedded/') || urlLike.includes('#/embedded/')) {
      return true;
    }

    try {
      const parsedUrl = new URL(urlLike, globalThis.location.origin ?? 'https://example.invalid');
      if ((parsedUrl.pathname ?? '').includes('/embedded/')) {
        return true;
      }
      if ((parsedUrl.hash ?? '').includes('#/embedded/')) {
        return true;
      }
      const embedded = parsedUrl.searchParams.get('embedded');
      return embedded === '1' || embedded === 'true';
    } catch {
      return false;
    }
  }
}
